import React, { useEffect, useMemo, useState } from 'react';

/**
 * SMS Templates & Events — admin-only, two-tab screen.
 * See docs/SRS_SMS_PROVIDER_CONFIG.md §16.
 *
 * Tab 1 — Templates: CRUD for the message body library ({placeholder}
 *         bodies + DLT template ids + declared variables_json).
 * Tab 2 — Events: registry of product events (LOGIN_OTP, RESERVATION_
 *         CONFIRM, etc.). Each event is bound to a template + optional
 *         provider + an enable toggle + a test-send helper.
 */

const DLT_CATEGORIES = [
  { key: 'TRANS', label: 'Transactional' },
  { key: 'PROMO', label: 'Promotional' },
  { key: 'SERVICE', label: 'Service' },
  { key: 'OTP', label: 'OTP' },
];

const emptyTpl = () => ({
  id: null, template_code: '', display_name: '', body: '',
  dlt_template_id: '', dlt_category: 'TRANS',
  variables_json: [], is_active: true,
});

export default function SmsTemplatesEvents({ token }) {
  const [tab, setTab] = useState('templates');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const headers = () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' });
  const clear = () => { setError(''); setMessage(''); };

  // ── Templates state ─────────────────────────────────────────────────────
  const [templates, setTemplates] = useState([]);
  const [providers, setProviders] = useState([]);
  const [tplForm, setTplForm] = useState(emptyTpl());
  const [tplEdit, setTplEdit] = useState(false);
  const [tplSaving, setTplSaving] = useState(false);
  const [tplPreview, setTplPreview] = useState('');

  const loadTemplates = async () => {
    try {
      const r = await fetch('/api/admin/sms-templates', { headers: headers() });
      const data = await r.json();
      if (r.ok) setTemplates(Array.isArray(data) ? data : []);
      else setError(data.error || 'Failed to load templates');
    } catch (err) { setError(err.message); }
  };

  const loadProviders = async () => {
    try {
      const r = await fetch('/api/admin/sms-providers', { headers: headers() });
      const data = await r.json();
      if (r.ok) setProviders(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  };

  // ── Events state ────────────────────────────────────────────────────────
  const [events, setEvents] = useState([]);
  const [eventDraft, setEventDraft] = useState({}); // { [event_code]: partial row with pending edits }
  const [testPanelFor, setTestPanelFor] = useState(null); // event_code
  const [testMobile, setTestMobile] = useState('');
  const [testVars, setTestVars] = useState({});        // { var_name: string }
  const [testResult, setTestResult] = useState(null);
  const [testRunning, setTestRunning] = useState(false);

  const loadEvents = async () => {
    try {
      const r = await fetch('/api/admin/sms-events', { headers: headers() });
      const data = await r.json();
      if (r.ok) setEvents(Array.isArray(data) ? data : []);
    } catch (err) { setError(err.message); }
  };

  useEffect(() => { loadTemplates(); loadProviders(); loadEvents(); }, []); // eslint-disable-line

  // ── Templates: save / delete / preview ──────────────────────────────────
  const varsToArray = (vars) => {
    // variables_json is stored as array of string keys; UI edits as array of {name}
    if (Array.isArray(vars)) return vars.filter(Boolean);
    if (vars && typeof vars === 'object') return Object.keys(vars);
    return [];
  };
  const extractPlaceholders = (body) => [...String(body || '').matchAll(/\{(\w+)\}/g)].map(m => m[1]);

  const startTplCreate = () => { clear(); setTplForm(emptyTpl()); setTplEdit(true); setTplPreview(''); };
  const startTplEdit = (t) => {
    clear();
    setTplForm({
      id: t.id,
      template_code: t.template_code || '',
      display_name: t.display_name || '',
      body: t.body || '',
      dlt_template_id: t.dlt_template_id || '',
      dlt_category: t.dlt_category || 'TRANS',
      variables_json: varsToArray(t.variables_json),
      is_active: t.is_active !== false,
    });
    setTplEdit(true);
    setTplPreview('');
  };
  const addTplVar = () => setTplForm(f => ({ ...f, variables_json: [...(f.variables_json || []), ''] }));
  const updateTplVar = (idx, v) => setTplForm(f => {
    const arr = [...(f.variables_json || [])]; arr[idx] = v; return { ...f, variables_json: arr };
  });
  const removeTplVar = (idx) => setTplForm(f => {
    const arr = [...(f.variables_json || [])]; arr.splice(idx, 1); return { ...f, variables_json: arr };
  });
  const autoDeclareFromBody = () => setTplForm(f => {
    const placeholders = extractPlaceholders(f.body);
    const existing = new Set(f.variables_json || []);
    const merged = [...(f.variables_json || []), ...placeholders.filter(p => !existing.has(p))];
    return { ...f, variables_json: merged };
  });

  const saveTpl = async () => {
    clear();
    if (!tplForm.template_code.trim() && !tplForm.id) return setError('template_code is required');
    if (!tplForm.body.trim()) return setError('body is required');
    const payload = {
      display_name: tplForm.display_name || null,
      body: tplForm.body,
      dlt_template_id: tplForm.dlt_template_id || null,
      dlt_category: tplForm.dlt_category || null,
      variables_json: (tplForm.variables_json || []).filter(Boolean),
      is_active: !!tplForm.is_active,
    };
    if (!tplForm.id) payload.template_code = tplForm.template_code.trim();
    setTplSaving(true);
    try {
      const url = tplForm.id ? `/api/admin/sms-templates/${tplForm.id}` : '/api/admin/sms-templates';
      const method = tplForm.id ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers: headers(), body: JSON.stringify(payload) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Save failed');
      setMessage(tplForm.id ? 'Template updated' : 'Template created');
      setTplEdit(false);
      await loadTemplates();
    } catch (err) { setError(err.message); }
    finally { setTplSaving(false); }
  };

  const deleteTpl = async (t) => {
    if (!window.confirm(`Delete template "${t.template_code}"? This is only allowed if no event references it.`)) return;
    try {
      const r = await fetch(`/api/admin/sms-templates/${t.id}`, { method: 'DELETE', headers: headers() });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || 'Delete failed'); }
      setMessage('Template deleted');
      await loadTemplates();
    } catch (err) { setError(err.message); }
  };

  const previewTpl = async () => {
    if (!tplForm.id && !tplForm.body) { setTplPreview(''); return; }
    const demo = {};
    for (const v of (tplForm.variables_json || [])) demo[v] = `<${v}>`;
    if (tplForm.id) {
      try {
        const r = await fetch(`/api/admin/sms-templates/${tplForm.id}/preview`, {
          method: 'POST', headers: headers(), body: JSON.stringify({ variables: demo }),
        });
        const data = await r.json();
        if (r.ok) setTplPreview(data.rendered);
      } catch { /* silent */ }
    } else {
      // local render for new templates
      const rendered = String(tplForm.body).replace(/\{(\w+)\}/g, (_, k) => demo[k] !== undefined ? demo[k] : `{${k}}`);
      setTplPreview(rendered);
    }
  };

  // ── Events: bind template / provider / toggle ───────────────────────────
  const eventRow = (code) => ({ ...(events.find(e => e.event_code === code) || {}), ...(eventDraft[code] || {}) });
  const setEventField = (code, field, value) => setEventDraft(d => ({
    ...d, [code]: { ...(d[code] || {}), [field]: value },
  }));
  const saveEvent = async (code) => {
    clear();
    const draft = eventDraft[code] || {};
    const base = events.find(e => e.event_code === code) || {};
    const payload = {};
    if (draft.template_id !== undefined) payload.template_id = draft.template_id || null;
    if (draft.provider_id !== undefined) payload.provider_id = draft.provider_id || null;
    if (draft.is_enabled !== undefined) payload.is_enabled = !!draft.is_enabled;
    if (draft.route_override !== undefined) payload.route_override = draft.route_override || null;
    if (draft.sender_id_override !== undefined) payload.sender_id_override = draft.sender_id_override || null;
    if (!Object.keys(payload).length) return;
    // Local enable guard — mirrors backend (friendlier UX)
    if (payload.is_enabled === true) {
      const tplId = draft.template_id !== undefined ? draft.template_id : base.template_id;
      if (!tplId) return setError(`Select a template before enabling ${code}`);
    }
    try {
      const r = await fetch(`/api/admin/sms-events/${code}`, {
        method: 'PUT', headers: headers(), body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Save failed');
      setMessage(`${code} saved`);
      setEventDraft(d => { const n = { ...d }; delete n[code]; return n; });
      await loadEvents();
    } catch (err) { setError(err.message); }
  };

  const openTestPanel = (code) => {
    setTestPanelFor(code === testPanelFor ? null : code);
    setTestMobile('');
    // seed vars based on the event's required_variables
    const ev = events.find(e => e.event_code === code);
    const vars = {};
    for (const v of (ev?.required_variables || [])) {
      // Smart default: for OTPs, use 123456
      vars[v] = /otp/i.test(v) ? '123456' : '';
    }
    setTestVars(vars);
    setTestResult(null);
  };
  const sendTest = async () => {
    if (!/^[6-9]\d{9}$/.test(testMobile)) { setError('10-digit Indian mobile (6-9 prefix) required'); return; }
    setTestRunning(true); setTestResult(null); clear();
    try {
      const r = await fetch(`/api/admin/sms-events/${testPanelFor}/test`, {
        method: 'POST', headers: headers(), body: JSON.stringify({ mobile: testMobile, variables: testVars }),
      });
      const data = await r.json();
      setTestResult(data);
    } catch (err) { setTestResult({ sent: false, error_message: err.message }); }
    finally { setTestRunning(false); }
  };

  // ── Render ──────────────────────────────────────────────────────────────
  const card = { background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: '1rem 1.2rem', marginBottom: '0.8rem' };
  const input = { padding: '0.5rem 0.7rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem', width: '100%', boxSizing: 'border-box' };
  const label = { display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: 4 };
  const btn = { padding: '0.42rem 0.85rem', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.82rem' };
  const templatesById = useMemo(() => {
    const m = {}; for (const t of templates) m[t.id] = t; return m;
  }, [templates]);

  return (
    <div style={{ padding: '1.2rem 1.4rem', background: '#f9fafb', minHeight: '80vh' }}>
      <div style={{ marginBottom: '1rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: '#1e3a8a' }}>SMS Templates & Events</h2>
        <div style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: 2 }}>
          Reusable message bodies + the events that trigger them (OTPs, booking confirmations, etc.).
        </div>
      </div>

      {error && <div style={{ ...card, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }}>{error}</div>}
      {message && <div style={{ ...card, background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0' }}>{message}</div>}

      {/* Tab switcher */}
      <div style={{ display: 'flex', gap: 6, marginBottom: '0.9rem', borderBottom: '1px solid #e2e8f0' }}>
        {[{ k: 'templates', l: 'Templates' }, { k: 'events', l: 'Events' }].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            style={{
              padding: '0.55rem 1.1rem', border: 'none', background: 'transparent',
              fontWeight: 700, fontSize: '0.92rem',
              color: tab === t.k ? '#1e40af' : '#64748b', cursor: 'pointer',
              borderBottom: tab === t.k ? '3px solid #1e40af' : '3px solid transparent',
              marginBottom: -1,
            }}>{t.l}</button>
        ))}
      </div>

      {/* ── Templates tab ── */}
      {tab === 'templates' && !tplEdit && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
            <h3 style={{ margin: 0 }}>Templates ({templates.length})</h3>
            <button onClick={startTplCreate} style={{ ...btn, background: '#1e40af', color: '#fff' }}>+ New Template</button>
          </div>
          {templates.length === 0 && (
            <div style={{ ...card, textAlign: 'center', color: '#64748b' }}>
              No templates yet. Create your first template — e.g. <b>LOGIN_OTP_V1</b> with body "Your Login OTP is {'{otp}'} Please do not share it. Easeventure".
            </div>
          )}
          {templates.map(t => (
            <div key={t.id} style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.6rem', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 260 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                    <span style={{ fontWeight: 700, color: '#0f172a', fontFamily: 'monospace', fontSize: '0.9rem' }}>{t.template_code}</span>
                    {t.display_name && <span style={{ color: '#475569', fontSize: '0.88rem' }}>· {t.display_name}</span>}
                    {t.dlt_category && <span style={{ padding: '2px 8px', borderRadius: 999, background: '#e0e7ff', color: '#3730a3', fontSize: '0.7rem', fontWeight: 700 }}>{t.dlt_category}</span>}
                    {!t.is_active && <span style={{ padding: '2px 8px', borderRadius: 999, background: '#fee2e2', color: '#991b1b', fontSize: '0.7rem', fontWeight: 700 }}>INACTIVE</span>}
                  </div>
                  <div style={{ padding: '0.55rem 0.75rem', background: '#f8fafc', borderRadius: 6, color: '#334155', fontSize: '0.86rem', marginBottom: 6, fontFamily: 'system-ui, monospace' }}>
                    {t.body}
                  </div>
                  <div style={{ fontSize: '0.73rem', color: '#94a3b8', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {t.dlt_template_id && <span>DLT: <b style={{ color: '#475569' }}>{t.dlt_template_id}</b></span>}
                    {varsToArray(t.variables_json).length > 0 && <span>Vars: {varsToArray(t.variables_json).map(v => <code key={v} style={{ background: '#fef3c7', padding: '0 5px', borderRadius: 4, marginLeft: 3 }}>{`{${v}}`}</code>)}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => startTplEdit(t)} style={{ ...btn, background: '#eff6ff', color: '#1e40af' }}>Edit</button>
                  <button onClick={() => deleteTpl(t)} style={{ ...btn, background: '#fef2f2', color: '#991b1b' }}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'templates' && tplEdit && (
        <div style={card}>
          <h3 style={{ marginTop: 0 }}>{tplForm.id ? `Edit Template #${tplForm.id}` : 'New Template'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.7rem', marginBottom: '0.7rem' }}>
            <div>
              <label style={label}>Template Code {tplForm.id && <span style={{ color: '#94a3b8', fontWeight: 400 }}>(read-only)</span>}</label>
              <input value={tplForm.template_code} readOnly={!!tplForm.id}
                onChange={e => setTplForm(f => ({ ...f, template_code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '').slice(0, 60) }))}
                placeholder="LOGIN_OTP_V1" style={{ ...input, fontFamily: 'monospace', background: tplForm.id ? '#f1f5f9' : '#fff' }} />
            </div>
            <div>
              <label style={label}>Display Name</label>
              <input value={tplForm.display_name} onChange={e => setTplForm(f => ({ ...f, display_name: e.target.value.slice(0, 120) }))} placeholder="Login OTP" style={input} />
            </div>
            <div>
              <label style={label}>DLT Template ID</label>
              <input value={tplForm.dlt_template_id} onChange={e => setTplForm(f => ({ ...f, dlt_template_id: e.target.value }))} placeholder="1707177651421899895" style={{ ...input, fontFamily: 'monospace', fontSize: '0.8rem' }} />
            </div>
            <div>
              <label style={label}>DLT Category</label>
              <select value={tplForm.dlt_category || ''} onChange={e => setTplForm(f => ({ ...f, dlt_category: e.target.value }))} style={input}>
                <option value="">—</option>
                {DLT_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '0.7rem' }}>
            <label style={label}>Body <span style={{ color: '#94a3b8' }}>— use <code style={{ background: '#fef3c7', padding: '0 4px', borderRadius: 4 }}>{`{variable}`}</code> for placeholders</span></label>
            <textarea value={tplForm.body} onChange={e => setTplForm(f => ({ ...f, body: e.target.value }))} rows={3}
              placeholder="Your Login OTP is {otp}. Valid for 5 min. Do not share."
              style={{ ...input, resize: 'vertical', fontFamily: 'system-ui, monospace', fontSize: '0.92rem' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              <span style={{ fontSize: '0.72rem', color: tplForm.body.length > 160 ? '#92400e' : '#94a3b8' }}>
                {tplForm.body.length} chars {tplForm.body.length > 160 && '· >160 = multiple SMS parts'}
              </span>
              <button type="button" onClick={autoDeclareFromBody} style={{ background: 'transparent', border: 'none', color: '#1e40af', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, textDecoration: 'underline' }}>
                Auto-detect placeholders from body →
              </button>
            </div>
          </div>

          <div style={{ marginBottom: '0.7rem' }}>
            <label style={label}>Declared Variables (each <code>{'{xxx}'}</code> in body must be listed)</label>
            {(tplForm.variables_json || []).length === 0 && (
              <div style={{ color: '#94a3b8', fontSize: '0.82rem', marginBottom: 6 }}>No variables declared.</div>
            )}
            {(tplForm.variables_json || []).map((v, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                <input value={v} onChange={e => updateTplVar(idx, e.target.value.replace(/[^a-z0-9_]/gi, ''))}
                  placeholder="otp"
                  style={{ ...input, fontFamily: 'monospace', maxWidth: 240 }} />
                <button type="button" onClick={() => removeTplVar(idx)} style={{ ...btn, background: '#fef2f2', color: '#991b1b' }}>Remove</button>
              </div>
            ))}
            <button type="button" onClick={addTplVar} style={{ ...btn, background: '#eff6ff', color: '#1e40af' }}>+ Add variable</button>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer', marginBottom: '0.6rem' }}>
            <input type="checkbox" checked={tplForm.is_active} onChange={e => setTplForm(f => ({ ...f, is_active: e.target.checked }))} /> Active
          </label>

          {tplPreview && (
            <div style={{ padding: '0.6rem 0.8rem', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 6, color: '#3730a3', fontSize: '0.88rem', marginBottom: '0.7rem' }}>
              <div style={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>Preview</div>
              {tplPreview}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
            <button type="button" onClick={previewTpl} style={{ ...btn, background: '#fff', color: '#1e40af', border: '1px solid #dbeafe' }}>Preview</button>
            <button type="button" onClick={() => setTplEdit(false)} style={{ ...btn, background: '#fff', color: '#334155', border: '1px solid #d1d5db' }}>Cancel</button>
            <button type="button" onClick={saveTpl} disabled={tplSaving} style={{ ...btn, background: '#1e40af', color: '#fff' }}>
              {tplSaving ? 'Saving…' : (tplForm.id ? 'Save changes' : 'Create template')}
            </button>
          </div>
        </div>
      )}

      {/* ── Events tab ── */}
      {tab === 'events' && (
        <div>
          <h3 style={{ margin: '0 0 0.6rem' }}>Events</h3>
          {events.map(ev => {
            const live = eventRow(ev.event_code);
            const testOpen = testPanelFor === ev.event_code;
            const tpl = templatesById[live.template_id];
            return (
              <div key={ev.event_code} style={{ ...card, borderLeft: live.is_enabled ? '4px solid #16a34a' : '4px solid #cbd5e1' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.8rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 280 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 800, fontFamily: 'monospace', color: '#0f172a' }}>{ev.event_code}</span>
                      <span style={{ color: '#475569', fontSize: '0.9rem' }}>— {ev.title}</span>
                      <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.04em',
                        background: live.is_enabled ? '#dcfce7' : '#f1f5f9',
                        color: live.is_enabled ? '#166534' : '#64748b' }}>
                        {live.is_enabled ? 'ENABLED' : 'DISABLED'}
                      </span>
                    </div>
                    <div style={{ color: '#64748b', fontSize: '0.82rem', margin: '4px 0 8px' }}>{ev.description}</div>
                    {ev.required_variables?.length > 0 && (
                      <div style={{ fontSize: '0.74rem', color: '#94a3b8' }}>
                        Required vars: {ev.required_variables.map(v => <code key={v} style={{ background: '#fef3c7', padding: '0 5px', borderRadius: 4, marginRight: 3 }}>{`{${v}}`}</code>)}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.6rem', marginTop: '0.7rem' }}>
                  <div>
                    <label style={label}>Template</label>
                    <select value={live.template_id || ''} onChange={e => setEventField(ev.event_code, 'template_id', e.target.value ? Number(e.target.value) : null)} style={input}>
                      <option value="">— none —</option>
                      {templates.filter(t => t.is_active).map(t => (
                        <option key={t.id} value={t.id}>{t.template_code}</option>
                      ))}
                    </select>
                    {tpl && (
                      <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 3, fontFamily: 'system-ui, monospace' }}>
                        {String(tpl.body).slice(0, 100)}{tpl.body.length > 100 ? '…' : ''}
                      </div>
                    )}
                  </div>
                  <div>
                    <label style={label}>Provider Override <span style={{ color: '#94a3b8', fontWeight: 400 }}>(leave blank for default)</span></label>
                    <select value={live.provider_id || ''} onChange={e => setEventField(ev.event_code, 'provider_id', e.target.value ? Number(e.target.value) : null)} style={input}>
                      <option value="">— Default provider —</option>
                      {providers.filter(p => p.is_active).map(p => (
                        <option key={p.id} value={p.id}>{p.display_name || p.provider_name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 6, marginTop: '0.75rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginRight: 'auto' }}>
                    <input type="checkbox"
                      checked={!!live.is_enabled}
                      onChange={e => setEventField(ev.event_code, 'is_enabled', e.target.checked)} /> Enabled
                  </label>
                  <button onClick={() => openTestPanel(ev.event_code)}
                    style={{ ...btn, background: testOpen ? '#fbbf24' : '#fef3c7', color: '#78350f', border: '1px solid #fde68a' }}>
                    {testOpen ? 'Close Test' : 'Test Send'}
                  </button>
                  <button onClick={() => saveEvent(ev.event_code)}
                    disabled={!eventDraft[ev.event_code]}
                    style={{ ...btn, background: eventDraft[ev.event_code] ? '#1e40af' : '#cbd5e1', color: '#fff' }}>
                    Save
                  </button>
                </div>

                {testOpen && (
                  <div style={{ marginTop: '0.7rem', padding: '0.7rem 0.9rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#78350f', marginBottom: 6 }}>Send Test · via full event pipeline</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 6, marginBottom: 6 }}>
                      <div>
                        <label style={label}>Mobile (10-digit)</label>
                        <input value={testMobile} onChange={e => setTestMobile(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="9840000000" style={input} />
                      </div>
                      {(ev.required_variables || []).map(v => (
                        <div key={v}>
                          <label style={label}><code style={{ background: '#fef3c7', padding: '0 5px', borderRadius: 4 }}>{`{${v}}`}</code></label>
                          <input value={testVars[v] || ''} onChange={e => setTestVars(vs => ({ ...vs, [v]: e.target.value }))} placeholder={v} style={input} />
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      <button onClick={sendTest} disabled={testRunning} style={{ ...btn, background: testRunning ? '#94a3b8' : '#f59e0b', color: '#fff' }}>
                        {testRunning ? 'Sending…' : 'Send Test'}
                      </button>
                      {testResult && (
                        <span style={{ fontSize: '0.82rem', color: testResult.sent ? '#065f46' : '#991b1b', fontWeight: 600 }}>
                          {testResult.sent ? '✓ SENT' : `✗ ${testResult.skipped || 'FAILED'}`}
                          {testResult.error_message ? ` — ${testResult.error_message}` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
